(ns hai.web-app
  "Main entry point for the Web App

  This is where we mount the react root and trigger any other initialization.
  "
  (:require
   [clojure.string :as str]
    ; [hai.builder-ide :as builder-ide]
    ;[hai.builder-ide.db :as builder-ide-db]
    ;hai.builder-ide.dev-util
   [hai.contacts.create :as contact-create]
   [hai.notes.create :as note-create]
   [hai.routes :as routes]
   [hai.ui.components :as c]
   [hai.ui.components.link :as link]
   [hai.ui.components.page-not-found :as page-not-found]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e >e! defevent]]
   [hai.ui.toast :as toast]
   hai.ui.tokens
   hai.ui.utils.debounce
   [hai.web-app.auth :as auth]
   [hai.web-app.connectivity :as connectivity]
   [hai.web-app.error-boundary :as error-boundary]
   hai.web-app.feature-flag
   [hai.web-app.components :as wc]
   [hai.web-app.layout :as layout]
   [hai.web-app.mobile :as mobile]
   [hai.web-app.routing :as routing]
   [hai.web-app.sync :as sync]
   [hai.web-app.theme :as theme]
   [hai.web-app.util.rf-storage :as rf-storage]
   [hai.web-app.viewport :as viewport]
   [hai.web-app.window :as window]
   [lambdaisland.glogi :as log]
   [lambdaisland.glogi.console :as glogi-console]
   [re-frame.core :as rf]
   [reagent.core :as reagent]
   [reagent.dom :as rdom]
   [reagent.dom.client :as rdom-client])
  (:require-macros
   [hai.ui.icons :as icons-macro]
   [hai.web-app.macros :as macros]))

;; (when ^boolean goog.DEBUG
;;   (require '[dataspex.core :as dataspex]))

(def build-stamp (macros/build-stamp))

(icons-macro/load-macro!)
#_(devtools/install!)
(glogi-console/install!)

(def use-legacy-render? false)

(defonce !root (atom nil))

(defn impersonator-warning []
  (let [user @(<s :auth/identity)]
    (when (= "impersonation" (-> user :token/claims :attested_by))
      [:div {:style {:color "white"
                     :background-color "red"
                     :padding "0.5rem"}}
       "You are impersonating " (:user/full-name user) " <" (first (:user/email user)) "> "
       [link/link {:on-click #(>e :auth/logout)} "Sign out"]])))

(defn main []
  ;; current-route is held as a ratom (not deref'd at binding) so view fns
  ;; receive a ratom prop they can `@`-deref themselves. The dispatcher
  ;; reads :data/:template inline via `@current-route`. NOTE(evgheni): the
  ;; ratom-prop contract spans ~70 view fns; until that's refactored to
  ;; subscribe directly, the dispatcher must keep passing the ratom.
  (let [current-route (<s :routing/current-route)
        mobile?       @(<s [::viewport/mobile?])
        dark-mode     @(<s :theme/dark-mode)
        auth-fetched? @(<s ::auth/fetched?)
        logged-in?    @(<s :auth/logged-in?)]
    (if-let [route @current-route]
      (do
        (log/debug ::current-route route)
        (when-let [{:keys [view mobile-view layout force-light-mode?]} (:data route)]
          (let [dark-mode?    false #_(and dark-mode (not force-light-mode?))
                chosen-view   (if (and mobile? mobile-view) mobile-view view)
                chosen-layout (cond
                                (false? layout) nil
                                layout          layout
                                mobile?         mobile/mobile-chrome
                                :else           layout/desktop-chrome)]
            [:div {:class (if dark-mode? "dark-theme" "light-theme")}
             [routing/window-title-component]
             [impersonator-warning]
             [c/Theme {:accentColor "violet"
                       :appearance (if dark-mode? "dark" "light")}
              (if (or logged-in?
                      (#{"/app/solutions/{solution/uuid}/view"
                         "/app/getting-started"} (:template route)))
                ;; Controller-only routes (e.g. the /desktop → /home redirect)
                ;; carry a truthy :data but no view; their controller navigates
                ;; away on the next tick. Guard against a nil chosen-view so we
                ;; never hand reagent a [nil …] hiccup form (nil tag → hard
                ;; crash, caught by the root error boundary) during that beat.
                (when chosen-view
                  (if chosen-layout
                    [chosen-layout [chosen-view current-route]]
                    [chosen-view current-route]))
                (if auth-fetched?
                  [wc/sign-in-page]
                  [wc/full-center
                   [wc/humble-loader]]))
              ;; Mounted here, not in the chrome — the notes chain pulls in
              ;; sharing.components, which itself requires the layout ns.
              [note-create/overlay]
              [contact-create/overlay]
              [toast/ToastWrapper]]])))
      [page-not-found/full-page {:dark-theme? dark-mode
                                 :home-url (routing/href :admin-ui/profile)
                                 :back! #(.back js/window.history)}])))

(defn render-root [root]
  ;; force re-render of root component, otherwise hot reloading breaks silently.
  ;; Wrapped in an error boundary so a render-time throw shows a recoverable
  ;; "Reload" screen instead of unmounting the whole tree (white screen of death).
  (rdom-client/render root [error-boundary/error-boundary [(fn [] main)]]))

(defn mount-root []
  (rf/clear-subscription-cache!)
  (if use-legacy-render?
    (rdom/render [main] (.getElementById js/document "app"))
    (if @!root
      (render-root @!root)
      (let [root-el (.getElementById js/document "app")
            root (rdom-client/create-root root-el)]
        (reset! !root root)
        (render-root @!root)))))

(defn reload-css []
  ;; NOTE (Donavan) I've made CSS reloading only load the Ornament derived stylesheet as this was
  ;; breaking Google fonts loading via stylesheet.
  (let [link (js/document.getElementById "ornament-stylesheet")
        href (.-href link)]
    (set! (.-href link)
          (str (str/replace href #"\?.*" "") "?" (js/Date.now)))))

(defevent ::initialize-db
  <-> [(rf-storage/settings-local)]
  [_old-db new-db]
  (or (when (seq _old-db) _old-db) new-db))

(def default-db
  {:routing/current-route :index})

(defn batch-changes
  [])

(defn init []
  (set! js/window.hai_has_loaded_db true)
  ;(log/set-levels {:glogi/root (if goog.DEBUG :debug :info)})
  (log/info :web-app/initializing build-stamp)
  (error-boundary/install-global-error-handlers!)
  (>e! [::initialize-db
        (merge default-db
               ;builder-ide/default-db
               )])
  (auth/init!)
  (theme/init!)
  (>e! :feature-flag/init)
  ;;(user/fetch-user!)
  (window/init!)
  (connectivity/init!)
  (sync/init!)
  (routing/init!
   (-> ["/app"
        {::routing/window-title "Humble AI"}
        ["" {:name        :admin-ui/root
             :controllers [{:start (fn [] (>e [:routing/navigate [:home/view]]))}]}]]
       (into (routes/all-routes))
        ;(into builder-ide/all-routes)
       ))
  ;(builder-ide-db/init!)
  ;(builder-ide/init!)
  (mount-root)
  #_(conn/connect!))

(when goog.DEBUG
  (defn ^:dev/after-load on-reload []
    (reload-css)
    (when-not js/window.hai_has_loaded_db
      (set! js/window.hai_has_loaded_db true)
      (init))
    (mount-root)
    ;; must be called last to prevent RF sub sweep from cleaning up hanging subs
    (js/hai_dev_preload_on_reload)))
